/**
 * Typed endpoints for /coach/admin/students.
 *
 * Transport, error class and status mapping come from `src/api/client.ts`
 * (ADR-0056). The `detail` code → field map and the operator-facing
 * messages below are what is genuinely student-specific.
 */
import { AdminApiError, request } from '../api/client.js'
import type {
  EmailAvailability,
  BulkResendInvitesBody,
  BulkResendInvitesResult,
  CreateEnrollmentBatchBody,
  EnrollmentBatchPreflightResponse,
  EnrollmentBatchPreviewBody,
  CreateEnrollmentBatchResponse,
  ContinuityRetryResponse,
  CreateEnrollmentBody,
  ExistingStudentEnrollmentBody,
  EnrollmentListResponse,
  EnrollmentMutationResponse,
  EnrollmentRow,
  InviteActionResult,
  StudentListResponse,
  StudentRow,
  ListStudentsParams,
  ReplaceEnrollmentBody,
  UpdateStudentBody,
} from './types.js'

export const adminStudentClient = {
  updateCompanyInformation(clientId: string, userId: string, body: {clientEmployeeId?: string | null; clientDepartment?: string | null}): Promise<StudentRow> {
    return request<StudentRow>(`/coach/admin/clients/${encodeURIComponent(clientId)}/students/${encodeURIComponent(userId)}/company-information`, {method: 'PATCH', body: JSON.stringify(body)})
  },
  /** Find or create the durable identity, then add one immutable period. */
  createEnrollment(
    clientId: string,
    body: CreateEnrollmentBody | ExistingStudentEnrollmentBody,
  ): Promise<EnrollmentMutationResponse> {
    return request<EnrollmentMutationResponse>(
      `/coach/admin/clients/${clientId}/enrollments`,
      { method: 'POST', body: JSON.stringify(body) },
    )
  },
  listEnrollments(
    clientId: string,
    userId: string,
  ): Promise<EnrollmentListResponse> {
    return request<EnrollmentListResponse>(
      `/coach/admin/clients/${clientId}/students/${userId}/enrollments`,
      { method: 'GET' },
    )
  },
  suspendEnrollment(
    clientId: string,
    enrollmentId: string,
    reason: string,
  ): Promise<EnrollmentRow> {
    return request<EnrollmentRow>(
      `/coach/admin/clients/${clientId}/enrollments/${enrollmentId}/suspend`,
      { method: 'POST', body: JSON.stringify({ reason }) },
    )
  },
  resumeEnrollment(clientId: string, enrollmentId: string): Promise<EnrollmentRow> {
    return request<EnrollmentRow>(
      `/coach/admin/clients/${clientId}/enrollments/${enrollmentId}/resume`,
      { method: 'POST' },
    )
  },
  cancelEnrollment(
    clientId: string,
    enrollmentId: string,
    reason: string,
  ): Promise<EnrollmentRow> {
    return request<EnrollmentRow>(
      `/coach/admin/clients/${clientId}/enrollments/${enrollmentId}/cancel`,
      { method: 'POST', body: JSON.stringify({ reason }) },
    )
  },
  earlyEndEnrollment(
    clientId: string,
    enrollmentId: string,
    reason: string,
  ): Promise<EnrollmentRow> {
    return request<EnrollmentRow>(
      `/coach/admin/clients/${clientId}/enrollments/${enrollmentId}/early-end`,
      { method: 'POST', body: JSON.stringify({ reason }) },
    )
  },
  replaceEnrollment(
    clientId: string,
    enrollmentId: string,
    body: ReplaceEnrollmentBody,
  ): Promise<EnrollmentMutationResponse> {
    return request<EnrollmentMutationResponse>(
      `/coach/admin/clients/${clientId}/enrollments/${enrollmentId}/replace`,
      { method: 'POST', body: JSON.stringify(body) },
    )
  },
  rescheduleEnrollment(
    clientId: string,
    enrollmentId: string,
    body: ReplaceEnrollmentBody,
  ): Promise<EnrollmentMutationResponse> {
    return request<EnrollmentMutationResponse>(
      `/coach/admin/clients/${clientId}/enrollments/${enrollmentId}/reschedule`,
      { method: 'POST', body: JSON.stringify(body) },
    )
  },
  retryEnrollmentContinuity(
    clientId: string,
    enrollmentId: string,
  ): Promise<ContinuityRetryResponse> {
    return request<ContinuityRetryResponse>(
      `/coach/admin/clients/${clientId}/enrollments/${enrollmentId}/continuity/retry`,
      { method: 'POST' },
    )
  },
  /** Find/create every identity, then add one cohort-backed enrollment each. */
  createEnrollmentBatch(
    clientId: string,
    body: CreateEnrollmentBatchBody,
  ): Promise<CreateEnrollmentBatchResponse> {
    return request<CreateEnrollmentBatchResponse>(
      `/coach/admin/clients/${clientId}/enrollment-batches`,
      { method: 'POST', body: JSON.stringify(body) },
    )
  },
  /**
   * Classify a mixed company wave before confirmation. The response may
   * describe same-client overlaps, but collapses foreign-client and
   * non-student identities to one non-enumerating conflict. Creation still
   * rechecks every condition atomically.
   */
  preflightEnrollmentBatch(
    clientId: string,
    body: EnrollmentBatchPreviewBody,
  ): Promise<EnrollmentBatchPreflightResponse> {
    return request<EnrollmentBatchPreflightResponse>(
      `/coach/admin/clients/${clientId}/enrollment-batches/preflight`,
      { method: 'POST', body: JSON.stringify(body) },
    )
  },
  /** Global roster — every client unless narrowed by params. */
  list(params: ListStudentsParams = {}): Promise<StudentListResponse> {
    const qs = new URLSearchParams()
    if (params.clientId) qs.set('clientId', params.clientId)
    if (params.cohortId) qs.set('cohortId', params.cohortId)
    // Explicit undefined check: `false` is a real value here, so a truthiness
    // test would silently drop the "customers only" filter.
    if (params.isInternal !== undefined)
      qs.set('isInternal', String(params.isInternal))
    if (params.inviteState) qs.set('inviteState', params.inviteState)
    if (params.enrollmentStatus) qs.set('enrollmentStatus', params.enrollmentStatus)
    if (params.startsOnOrAfter) qs.set('startsOnOrAfter', params.startsOnOrAfter)
    if (params.startsOnOrBefore) qs.set('startsOnOrBefore', params.startsOnOrBefore)
    if (params.lifecycleStage)
      qs.set('lifecycleStage', params.lifecycleStage)
    if (params.needsAttention !== undefined)
      qs.set('needsAttention', String(params.needsAttention))
    if (params.attentionReason)
      qs.set('attentionReason', params.attentionReason)
    if (params.search) qs.set('search', params.search)
    if (params.limit !== undefined) qs.set('limit', String(params.limit))
    if (params.offset !== undefined) qs.set('offset', String(params.offset))
    const suffix = qs.size > 0 ? `?${qs.toString()}` : ''
    return request<StudentListResponse>(`/coach/admin/students${suffix}`, {
      method: 'GET',
    })
  },
  get(userId: string): Promise<StudentRow> {
    return request<StudentRow>(`/coach/admin/students/${userId}`, {
      method: 'GET',
    })
  },
  update(userId: string, patch: UpdateStudentBody): Promise<StudentRow> {
    return request<StudentRow>(`/coach/admin/students/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    })
  },
  /**
   * Resend the invite for a whole selection — the recovery for an outage
   * mid-wave, paired with the roster's `inviteState: 'failed'` filter.
   *
   * Per-row outcomes, NOT all-or-nothing (unlike the other bulk verbs):
   * an email that already left cannot be rolled back because a later row
   * failed. Each row runs the full single-resend sequence, so the
   * per-student rate limit and the eligibility gate hold per row.
   */
  bulkResendInvites(
    clientId: string,
    body: BulkResendInvitesBody,
  ): Promise<BulkResendInvitesResult> {
    return request<BulkResendInvitesResult>(
      `/coach/admin/clients/${clientId}/student-invite-resends`,
      { method: 'POST', body: JSON.stringify(body) },
    )
  },
  /**
   * Supersede and queue a delivery-owned invite, invalidating the previous
   * links. Pre-ledger recipients retain the direct-send compatibility path
   * until the coordinated cutover.
   *
   * The most-used action here by volume ("they say they never got it"), and
   * the fix after a bounced address is corrected. Rate-limited server-side
   * on the STUDENT, so two operators on the same ticket cannot double the
   * mail reaching one inbox.
   */
  resendInvite(userId: string): Promise<InviteActionResult> {
    return request<InviteActionResult>(
      `/coach/admin/students/${userId}/invite/resend`,
      { method: 'POST' },
    )
  },
  /**
   * Is this email free to register? Advisory only — the create route
   * re-checks and the unique index is the backstop, so a "free" answer is
   * never a promise. The server answers a bare boolean and never names the
   * client holding a taken address (R5).
   */
  checkEmailAvailable(email: string): Promise<EmailAvailability> {
    const qs = new URLSearchParams({ email })
    return request<EmailAvailability>(
      `/coach/admin/students/email-available?${qs.toString()}`,
      { method: 'GET' },
    )
  },
}

/**
 * Which FIELD each server error belongs to, so the message renders at that
 * field instead of in a summary below the submit button.
 *
 * Keys are the server's stable `detail` codes. Anything absent falls back to
 * a form-level message — see `src/common/field-errors.tsx`.
 */
export const STUDENT_ERROR_FIELD = {
  email_already_registered: 'email',
  cohort_not_found: 'cohortId',
  cohort_access_ended: 'cohortId',
  access_start_in_past: 'scheduledStartOn',
  access_end_required: 'accessEndsOn',
  access_end_in_past: 'accessEndsOn',
  access_window_invalid: 'accessEndsOn',
  invalid_cohort_id: 'cohortId',
} as const

export type StudentField =
  (typeof STUDENT_ERROR_FIELD)[keyof typeof STUDENT_ERROR_FIELD]

/** Map the server's `detail` code to an operator-readable message. */
export function messageForStudentError(err: unknown): string {
  if (err instanceof AdminApiError) {
    switch (err.detail) {
      case 'batch_preview_changed':
        return 'The import preview has changed. Check rows again and review the updated dates, settings and student results before creating enrollments.'
      case 'enrollment_write_failed':
        return 'The enrollments could not be created. Your rows are still here; try again. If the problem continues, contact support.'
      case 'student_identity_confirmed':
        return 'The student has confirmed this identity. They manage these fields in their Account.'
      case 'student_identity_version_conflict':
        return 'This identity changed since you opened it. Reload the page before editing.'
      case 'email_already_registered':
        // Deliberately does not say WHERE. One human, one client — and
        // which client holds it is not ours to disclose (R5).
        return 'That email is already registered.'
      case 'period_overlap':
        return 'This period overlaps another non-cancelled enrollment for the student.'
      case 'terms_incomplete':
        return 'Complete every enrollment term before creating the period.'
      case 'program_not_found':
      case 'program_unavailable':
        return 'That program is no longer available. Choose an active program.'
      case 'program_level_unavailable':
      case 'program_level_mismatch':
        return 'That level is unavailable for the selected program. Check the placement and program selection.'
      case 'program_not_ready':
        return 'This program is not ready for enrollment. Check its diagnostic, default tutor and lessons at every supported level.'
      case 'continuity_source_ineligible':
        return 'That earlier period cannot seed this continuation.'
      case 'continuity_source_consumed':
        return 'That earlier period is already reserved for another continuation.'
      case 'continuity_placement_source_missing':
        return 'No eligible diagnostic result can be reused for these terms.'
      case 'enrollment_requires_early_end':
        return 'Participation has started; end the period early instead of cancelling it.'
      case 'enrollment_cancel_instead':
        return 'No participation is recorded yet; cancel this period instead of ending it.'
      case 'cohort_not_found':
        return 'That cohort no longer exists in this client.'
      case 'invalid_cohort_id':
        return 'That cohort reference is not valid.'
      case 'client_not_found':
        return 'That client no longer exists — reload and pick another.'
      case 'student_not_found':
        return 'That student no longer exists.'
      case 'admin_required':
        return 'You do not have permission to manage students.'
      case 'cohort_access_ended':
        return 'That cohort has already ended. Choose an active cohort, extend its access end, or choose No cohort and set an individual access end.'
      case 'access_start_in_past':
        return 'Access must start today or later. Leave the date blank to start immediately, or choose a future date.'
      case 'access_end_required':
        return 'Set an access end date. A student without a cohort cannot have unbounded access.'
      case 'access_end_in_past':
        return 'Access must end today or later. Choose a valid end date, or assign an active cohort.'
      case 'access_window_invalid':
        return 'Access cannot end before it starts. Check the student and cohort dates.'
      case 'invite_not_available':
        return 'This student cannot receive an activation email right now — they have already activated, have no eligible enrollment, or enrollment access has ended.'
      case 'access_window_closed':
        return 'That enrollment has already ended, so an invitation would arrive as a dead link. Create a new enrollment before sending another activation email.'
      case 'rate_limited':
        return 'Too many invitations sent to this student. Wait a few minutes and try again.'
      case 'invite_already_sent':
        return 'Their activation email has already gone out, so there is no scheduled send left to change. Resend it to issue a fresh link.'
      case 'no_invite_schedule_change':
        return 'Nothing to change — pick a new send date or cancel the scheduled send.'
      case 'invalid_email':
        return 'Some rows do not carry a valid email address.'
      case 'client_employee_id_disabled':
        return 'Employee ID is disabled for this client. Enable it in client settings or leave it blank.'
      case 'client_department_disabled':
        return 'Department is disabled for this client. Enable it in client settings or leave it blank.'
      case 'client_company_information_conflict':
        return 'Company information differs from the saved student record. Correct the input or edit the student’s company information first.'
      case 'invalid_name':
        return 'Some rows have an empty name.'
      case 'duplicate_emails_in_batch':
        return 'Some addresses appear more than once in the list.'
      default:
        return err.message
    }
  }
  return err instanceof Error ? err.message : 'Request failed'
}
